export type CrmContactIntakeAnswerTone = "neutral" | "info" | "success" | "warning" | "danger";

export interface CrmContactIntakeAnswer {
  key: string;
  label: string;
  value: string;
  description: string | null;
  category: string | null;
  tone: CrmContactIntakeAnswerTone;
}

export interface CrmContactIntakeAnswerSet {
  key: string;
  label: string;
  summary: string | null;
  source: string | null;
  status: string | null;
  completedAt: string | null;
  tone: CrmContactIntakeAnswerTone;
  answers: CrmContactIntakeAnswer[];
}

export interface BuildCrmContactIntakeAnswersOptions {
  intakeKeys?: readonly string[];
  maxSets?: number;
  maxAnswersPerSet?: number;
}

export const CRM_CONTACT_INTAKE_ANSWER_FIELD_KEYS = [
  "crmContactIntakeAnswers",
  "contactIntakeAnswers",
  "intakeAnswers",
  "qualificationAnswers",
  "qualificationFormAnswers",
] as const;

const PRIVATE_KEY_PATTERN = /(?:password|secret|token|authorization|credential|private[_-]?key|api[_-]?key)/i;
const MAX_TEXT_LENGTH = 220;

export function buildCrmContactIntakeAnswers(
  customFields: unknown,
  options: BuildCrmContactIntakeAnswersOptions = {},
): CrmContactIntakeAnswerSet[] {
  const fields = asRecord(customFields);
  if (!fields) return [];

  const intakeKeys = options.intakeKeys?.length
    ? [...options.intakeKeys]
    : [...CRM_CONTACT_INTAKE_ANSWER_FIELD_KEYS];
  const maxSets = normalizePositiveInteger(options.maxSets, 6);
  const maxAnswersPerSet = normalizePositiveInteger(options.maxAnswersPerSet, 16);

  return intakeKeys
    .flatMap((key) => parseIntakeAnswerSets(key, fields[key], maxAnswersPerSet))
    .filter((set): set is CrmContactIntakeAnswerSet => Boolean(set))
    .slice(0, maxSets);
}

function parseIntakeAnswerSets(
  rootKey: string,
  value: unknown,
  maxAnswersPerSet: number,
): Array<CrmContactIntakeAnswerSet | null> {
  if (Array.isArray(value)) {
    if (value.some((entry) => hasIntakeSetShape(asRecord(entry)))) {
      return value.map((entry, index) => parseIntakeAnswerSet(`${rootKey}.${index}`, entry, maxAnswersPerSet));
    }

    return [parseIntakeAnswerSet(rootKey, {
      key: rootKey,
      label: formatLabel(rootKey),
      answers: value,
    }, maxAnswersPerSet)];
  }

  const record = asRecord(value);
  if (!record) return [];

  if (hasIntakeSetShape(record)) {
    return [parseIntakeAnswerSet(rootKey, record, maxAnswersPerSet)];
  }

  return Object.entries(record)
    .map(([key, entry]) => parseIntakeAnswerSet(key, entry, maxAnswersPerSet));
}

function parseIntakeAnswerSet(
  fallbackKey: string,
  value: unknown,
  maxAnswersPerSet: number,
): CrmContactIntakeAnswerSet | null {
  if (isPrivateKey(fallbackKey)) return null;

  const record = asRecord(value);
  if (!record) return null;

  const key = readString(record.key) ?? fallbackKey;
  if (isPrivateKey(key)) return null;

  const answers = parseIntakeAnswers(record.answers ?? record.items ?? record.fields ?? record.questions ?? record, maxAnswersPerSet);
  if (answers.length === 0) return null;

  const label = truncate(
    readString(record.label)
    ?? readString(record.title)
    ?? readString(record.name)
    ?? formatLabel(key),
  ) ?? formatLabel(key);

  return {
    key,
    label,
    summary: truncate(readString(record.summary) ?? readString(record.description)),
    source: truncate(readString(record.source) ?? readString(record.channel) ?? readString(record.formType)),
    status: truncate(readString(record.status) ?? readString(record.outcome)),
    completedAt: normalizeTimestamp(
      readString(record.completedAt)
      ?? readString(record.submittedAt)
      ?? readString(record.createdAt)
      ?? readString(record.timestamp)
      ?? readString(record.at),
    ),
    tone: normalizeTone(readString(record.tone) ?? readString(record.status) ?? readString(record.outcome)),
    answers,
  };
}

function parseIntakeAnswers(value: unknown, maxAnswers: number): CrmContactIntakeAnswer[] {
  if (Array.isArray(value)) {
    return value
      .map((entry, index) => parseIntakeAnswer(`answer-${index + 1}`, entry))
      .filter((answer): answer is CrmContactIntakeAnswer => Boolean(answer))
      .slice(0, maxAnswers);
  }

  const record = asRecord(value);
  if (!record) return [];

  const ignoredSetKeys = new Set([
    "key",
    "label",
    "title",
    "name",
    "summary",
    "description",
    "source",
    "channel",
    "formType",
    "status",
    "outcome",
    "completedAt",
    "submittedAt",
    "createdAt",
    "timestamp",
    "at",
    "tone",
    "answers",
    "items",
    "fields",
    "questions",
  ]);

  return Object.entries(record)
    .filter(([key]) => !ignoredSetKeys.has(key))
    .map(([key, entry]) => parseIntakeAnswer(key, entry))
    .filter((answer): answer is CrmContactIntakeAnswer => Boolean(answer))
    .slice(0, maxAnswers);
}

function parseIntakeAnswer(
  fallbackKey: string,
  value: unknown,
): CrmContactIntakeAnswer | null {
  if (isPrivateKey(fallbackKey)) return null;

  const record = asRecord(value);
  const key = record ? readString(record.key) ?? fallbackKey : fallbackKey;
  if (isPrivateKey(key)) return null;

  const label = record
    ? truncate(
      readString(record.label)
      ?? readString(record.question)
      ?? readString(record.title)
      ?? formatLabel(key),
    ) ?? formatLabel(key)
    : formatLabel(key);
  const rawValue = record && "value" in record
    ? record.value
    : record?.answer ?? record?.answers ?? record?.displayValue ?? record?.response ?? value;
  const formattedValue = formatAnswerValue(rawValue);
  if (!formattedValue) return null;

  return {
    key,
    label,
    value: formattedValue,
    description: record
      ? truncate(readString(record.description) ?? readString(record.helpText) ?? readString(record.evidence))
      : null,
    category: record
      ? truncate(readString(record.category) ?? readString(record.section) ?? readString(record.group))
      : null,
    tone: normalizeTone(record ? readString(record.tone) ?? readString(record.status) : null),
  };
}

function hasIntakeSetShape(record: Record<string, unknown> | null): boolean {
  if (!record) return false;
  return [
    "answers",
    "items",
    "fields",
    "questions",
    "completedAt",
    "submittedAt",
    "formType",
    "source",
  ].some((key) => key in record);
}

function formatAnswerValue(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "string") return truncate(value.trim());
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : null;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) {
    const values = value
      .map(formatAnswerValue)
      .filter((entry): entry is string => Boolean(entry));
    if (values.length === 0) return null;
    const preview = values.slice(0, 5).join(", ");
    return value.length > 5 ? `${preview}, +${value.length - 5} more` : preview;
  }

  const record = asRecord(value);
  if (!record) return null;
  return truncate(
    readString(record.displayValue)
    ?? readString(record.label)
    ?? readString(record.value)
    ?? readString(record.summary),
  );
}

function normalizeTone(value: string | null): CrmContactIntakeAnswerTone {
  if (value === "success" || value === "warning" || value === "danger" || value === "info") return value;
  if (value === "critical" || value === "error" || value === "failed" || value === "blocked") return "danger";
  if (value === "ready" || value === "passed" || value === "complete" || value === "completed") return "success";
  if (value === "partial" || value === "review" || value === "needs_review") return "warning";
  return "neutral";
}

function normalizeTimestamp(value: string | null): string | null {
  if (!value) return null;
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return null;
  return new Date(timestamp).toISOString();
}

function normalizePositiveInteger(value: number | undefined, fallback: number): number {
  return typeof value === "number" && Number.isInteger(value) && value > 0 ? value : fallback;
}

function formatLabel(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function truncate(value: string | null): string | null {
  if (!value) return null;
  return value.length > MAX_TEXT_LENGTH ? `${value.slice(0, MAX_TEXT_LENGTH - 3)}...` : value;
}

function readString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function isPrivateKey(key: string): boolean {
  return PRIVATE_KEY_PATTERN.test(key);
}
